import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import TagTextField from "@/features/copy-tags/ui/tag-text-field";
import CopyPreview from "@/features/copy-tags/ui/copy-preview";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CopyContext,
  listCopyProducts,
} from "@/features/copy-tags/services/copy-tags-api";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";

const ALL_REGIONS = "__all__";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface DescriptionSection {
  subTitle: string;
  content: string[];
  additionalConclusion?: string;
}

// Friendly editor for a managed event's scheduling description — a list of
// sections (heading + bullet lines + optional closing line). Stored in the
// managed event's metadata as `schedulingDescription`.
export default function SchedulingDescriptionField({
  value,
  onChange,
  copyContext,
}: {
  value: DescriptionSection[];
  onChange: (next: DescriptionSection[]) => void;
  // When given, each field gets an "Insert price" menu and the editor a
  // preview — price tags resolve per customer (RH-1007).
  copyContext?: CopyContext;
}) {
  // Booking copy is shared by every product that uses this session type, and
  // short names live on each product. Pick one to insert and preview with —
  // saving still checks the names against all of them.
  const managedEventId = copyContext?.managedEventId ?? null;
  const { data: products = [] } = useQuery({
    queryKey: ["copy-products", managedEventId],
    queryFn: () => listCopyProducts(managedEventId as string),
    enabled: !!managedEventId,
  });
  const [productId, setProductId] = useState<string | null>(null);
  // One region choice drives the Insert price menu and the preview; none →
  // every region (menu: a fee anywhere; preview: one block per region).
  const [regionId, setRegionId] = useState<string | null>(null);
  const regions = useActiveRegions().data ?? [];
  useEffect(() => {
    if (products.length && !products.some((p) => p.id === productId)) {
      setProductId(products[0].id);
    }
  }, [products, productId]);
  const context: CopyContext | undefined = copyContext && {
    ...copyContext,
    productId,
    regionId,
  };

  // A text field — tag-aware when there is a copy context.
  const field = (
    v: string,
    set: (next: string) => void,
    placeholder: string,
    multiline?: boolean,
  ) =>
    context ? (
      <TagTextField
        multiline={multiline}
        rows={3}
        context={context}
        placeholder={placeholder}
        value={v}
        onChange={set}
        className={multiline ? "min-h-[70px] text-sm" : undefined}
      />
    ) : multiline ? (
      <textarea
        className="w-full min-h-[70px] rounded-md border border-gray-200 p-2 text-sm"
        placeholder={placeholder}
        value={v}
        onChange={(e) => set(e.target.value)}
      />
    ) : (
      <Input
        className="h-8 text-sm"
        placeholder={placeholder}
        value={v}
        onChange={(e) => set(e.target.value)}
      />
    );

  const patch = (i: number, p: Partial<DescriptionSection>) =>
    onChange(value.map((s, idx) => (idx === i ? { ...s, ...p } : s)));
  const add = () =>
    onChange([...value, { subTitle: "", content: [], additionalConclusion: "" }]);
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Scheduling description{" "}
          <span className="text-gray-400">(shown on the booking screen)</span>
        </p>
        <Button type="button" size="sm" variant="outline" onClick={add}>
          <Plus className="h-4 w-4 mr-1" /> Add section
        </Button>
      </div>

      {copyContext && (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-gray-50 p-2">
          <span className="text-[11px] text-gray-500">Prices for product</span>
          {products.length ? (
            <Select
              value={productId ?? undefined}
              onValueChange={(v) => setProductId(v)}
            >
              <SelectTrigger className="h-7 w-56 text-xs">
                <SelectValue placeholder="Product" />
              </SelectTrigger>
              <SelectContent>
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="text-xs">
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-[11px] text-gray-400">
              {managedEventId
                ? "No product uses this session type yet — only the plain tags are available."
                : "Save first to pick a product."}
            </span>
          )}
          <span className="text-[11px] text-gray-500 ml-2">Region</span>
          <Select
            value={regionId ?? ALL_REGIONS}
            onValueChange={(v) => setRegionId(v === ALL_REGIONS ? null : v)}
          >
            <SelectTrigger className="h-7 w-40 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_REGIONS} className="text-xs">
                All regions
              </SelectItem>
              {regions.map((r) => (
                <SelectItem key={r.id} value={r.id} className="text-xs">
                  {r.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {products.length > 1 && (
            <span className="text-[10px] text-gray-400 w-full">
              This copy is shared by {products.length} products. Named tags
              must exist on all of them to save.
            </span>
          )}
        </div>
      )}

      {value.length === 0 ? (
        <p className="text-xs text-gray-400">No sections yet.</p>
      ) : (
        value.map((section, i) => (
          <div
            key={i}
            className="rounded-md border border-gray-200 p-3 space-y-2 bg-white"
          >
            <div className="flex items-center justify-between">
              <Badge variant="outline">Section {i + 1}</Badge>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => remove(i)}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>

            <div>
              <p className="text-[11px] text-gray-500 mb-1">Heading</p>
              {field(
                section.subTitle,
                (v) => patch(i, { subTitle: v }),
                "e.g. What to expect",
              )}
            </div>

            <div>
              <p className="text-[11px] text-gray-500 mb-1">
                Bullets (one per line)
              </p>
              {field(
                section.content.join("\n"),
                (v) => patch(i, { content: v.split("\n") }),
                "50-minute video session\nBring any prior reports",
                true,
              )}
            </div>

            <div>
              <p className="text-[11px] text-gray-500 mb-1">
                Closing line (optional, italic)
              </p>
              {field(
                section.additionalConclusion ?? "",
                (v) => patch(i, { additionalConclusion: v }),
                "e.g. You can reschedule up to 24h before",
              )}
            </div>
          </div>
        ))
      )}

      {context && value.length > 0 && (
        <CopyPreview
          context={context}
          allRegionsWhenUnset
          getValue={() => value}
        />
      )}
    </div>
  );
}
